import React, { FC } from "react";
import {
  Calendar,
  Clock,
  MessageCircle,
  FileText,
  TrendingUp,
  TrendingDown,
  Target,
  Award,
  AlertTriangle,
  Download,
} from "lucide-react";
import { motion } from "framer-motion";
import Overlay from "../../../../checkEligibilty/components/modals/Overlay";
import { ContainerVariants } from "../../../../checkEligibilty/components/modals/variant";
import { createPortal } from "react-dom";
import { MdOutlineFeedback } from "react-icons/md";
import useOpener from "../../../../../../hooks/useOpener";
import FeedbackModal from "../../components/FeedbackModal";
import { IoMdClose } from "react-icons/io";
import { useGetSessionAnalysis } from "../../../../../../hooks/ai/useAI";
import RequestWrapper from "../../../../components/RequestWrapper";
import { IoCloudDownloadOutline } from "react-icons/io5";
import { PDFDownloadLink } from "@react-pdf/renderer";
import AnalyticsPDFDocument from "./AnalyticsPDFDocument";
interface Props {
  onClose: () => void;
  onStartNewSession: () => void;
  downloadUrl: string;
  sessionTemp: string;
  fibiClientToken?: string;
}

const AnalyticsModal: FC<Props> = ({
  onClose,
  downloadUrl,
  sessionTemp,
  fibiClientToken,
  onStartNewSession,
}) => {
  const {
    data: sessionAnalysisData,
    isLoading,
    isError,
  } = useGetSessionAnalysis(sessionTemp, fibiClientToken);

  const {
    onClose: feedbackOnClose,
    onOpen: feedbackOnOpen,
    open: feedbackOpen,
  } = useOpener();
  const getScoreColor = (score: number): string => {
    if (score >= 80) return "text-green-600 bg-green-50";
    if (score >= 70) return "text-blue-600 bg-blue-50";
    if (score >= 60) return "text-orange-600 bg-orange-50";
    return "text-red-600 bg-red-50";
  };

  const getScoreBarColor = (score: number): string => {
    if (score >= 80) return "bg-green-500";
    if (score >= 70) return "bg-blue-500";
    if (score >= 60) return "bg-orange-500";
    return "bg-red-500";
  };

  return createPortal(
    <Overlay onClose={onClose}>
      <motion.div
        onClick={(e) => e.stopPropagation()}
        variants={ContainerVariants}
        className="w-[95vw] h-[90vh] bg-white rounded-xl sm:px-6 flex flex-col"
      >
        {/* Header - now outside the scrollable area */}
        <div className="bg-white rounded-t-xl shadow-xs py-6 border-b border-gray-200 relative px-10">
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
            <div>
              <h1 className="text-3xl font-bold text-gray-900 mb-2">
                Interview Analytics
              </h1>
              <p className="text-gray-600">
                Comprehensive analysis of your interview performance
              </p>
            </div>
            <div className="flex gap-3 items-center flex-wrap">
              {sessionAnalysisData && (
                <PDFDownloadLink
                  document={
                    <AnalyticsPDFDocument
                      sessionAnalysisData={sessionAnalysisData}
                    />
                  }
                  fileName={`interview-analytics-${sessionTemp}.pdf`}
                  className="flex items-center justify-center gap-2 px-4 py-2 bg-white text-[#0076E9] rounded-lg hover:border-[#0076E9]/80 border border-[#0076E9] transition-colors w-full sm:w-max"
                >
                  <IoCloudDownloadOutline className="w-4 h-4" />
                  Download Analytics
                </PDFDownloadLink>
              )}

              <a
                href={downloadUrl}
                className="flex items-center justify-center gap-2 px-4 py-2 bg-[#0076E9] text-white rounded-lg hover:bg-[#0076E9]/80 transition-colors w-full sm:w-max"
                download="fibi-session.pdf"
              >
                <Download className="w-4 h-4" />
                Download session
              </a>

              <button
                className="flex items-center justify-center gap-2 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors w-full sm:w-max"
                onClick={feedbackOnOpen}
              >
                <MdOutlineFeedback className="w-4 h-4" />
                Give Feedback
              </button>
            </div>
          </div>

          {/* Close button */}
          <IoMdClose
            onClick={onClose}
            className="ml-2 cursor-pointer absolute right-2 sm:-right-2 top-5 bg-white"
            size={24}
          />
        </div>

        <div className="flex-1 bg-gray-50 py-8 sm:px-4 relative overflow-y-auto">
          <div className="w-full mx-auto h-full">
            {/* <div className="h-full flex-1 bg-red-300"> */}
            <RequestWrapper
              data={sessionAnalysisData}
              isLoading={isLoading}
              isError={isError}
              loadingText={"Analyzing your session, just a moment…"}
            >
              <>
                {sessionAnalysisData && (
                  <>
                    {/* Session Summary */}
                    <div className="bg-white rounded-xl shadow-xs p-6 mb-8">
                      <h2 className="text-xl font-bold text-gray-900 mb-6 flex items-center gap-2">
                        <FileText className="w-5 h-5 text-blue-600" />
                        Session Summary
                      </h2>

                      <div className="mb-6 border-y py-6">
                        <div className="flex items-center justify-between mb-4">
                          <h3 className="text-lg font-semibold text-gray-900">
                            Overall Performance
                          </h3>
                          <div
                            className={`px-4 py-2 rounded-full text-lg font-bold ${getScoreColor(
                              sessionAnalysisData.sessionData.overallScore
                            )}`}
                          >
                            {sessionAnalysisData.sessionData.overallScore}%
                          </div>
                        </div>
                        {/* overall performaance bar */}
                        <div className="w-full bg-gray-200 rounded-full h-3 mb-4">
                          <div
                            className={`h-3 rounded-full transition-all duration-700 ${getScoreBarColor(
                              sessionAnalysisData.sessionData.overallScore
                            )}`}
                            style={{
                              width: `${sessionAnalysisData.sessionData.overallScore}%`,
                            }}
                          ></div>
                        </div>
                        <p className="text-gray-700 leading-relaxed">
                          {sessionAnalysisData.sessionData.summary}
                        </p>
                      </div>

                      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                        <div className="flex items-center gap-3">
                          <div className="p-2 bg-blue-50 rounded-lg">
                            <Calendar className="w-5 h-5 text-blue-600" />
                          </div>
                          <div>
                            <p className="text-sm text-gray-500">Date & Time</p>
                            <p className="font-semibold text-gray-900">
                              {sessionAnalysisData.sessionData.date}
                            </p>
                          </div>
                        </div>

                        <div className="flex items-center gap-3">
                          <div className="p-2 bg-green-50 rounded-lg">
                            <Clock className="w-5 h-5 text-green-600" />
                          </div>
                          <div>
                            <p className="text-sm text-gray-500">Duration</p>
                            <p className="font-semibold text-gray-900">
                              {sessionAnalysisData.sessionData.duration}
                            </p>
                          </div>
                        </div>

                        <div className="flex items-center gap-3">
                          <div className="p-2 bg-orange-50 rounded-lg">
                            <MessageCircle className="w-5 h-5 text-orange-600" />
                          </div>
                          <div>
                            <p className="text-sm text-gray-500">
                              Questions Asked
                            </p>
                            <p className="font-semibold text-gray-900">
                              {sessionAnalysisData.sessionData.totalQuestions}
                            </p>
                          </div>
                        </div>

                        <div className="flex items-center gap-3">
                          <div className="p-2 bg-purple-50 rounded-lg">
                            <Target className="w-5 h-5 text-purple-600" />
                          </div>
                          <div>
                            <p className="text-sm text-gray-500">
                              Interview Type
                            </p>
                            <p className="font-semibold text-gray-900">
                              {sessionAnalysisData.sessionData.interviewType}
                            </p>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Behavioral Insights */}
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-8">
                      {/* Strongest Responses */}
                      <div className="bg-white rounded-xl shadow-xs p-6">
                        <h2 className="text-xl font-bold text-gray-900 mb-6 flex items-center gap-2">
                          <TrendingUp className="w-5 h-5 text-green-600" />
                          Strongest Responses
                        </h2>
                        <div className="space-y-4">
                          {sessionAnalysisData.strongestResponses &&
                          sessionAnalysisData.strongestResponses.length > 0 ? (
                            sessionAnalysisData.strongestResponses.map(
                              (response, index) => (
                                <div
                                  key={index}
                                  className="border border-green-100 rounded-lg p-4 bg-green-50/30"
                                >
                                  <div className="flex items-start justify-between mb-2">
                                    <div className="flex items-center gap-2">
                                      <span className="bg-green-600 text-white text-xs px-2 py-1 rounded-full font-semibold">
                                        #{index + 1}
                                      </span>
                                      <span
                                        className={`px-2 py-1 rounded-full text-sm font-semibold ${getScoreColor(
                                          response.score
                                        )}`}
                                      >
                                        {response.score}%
                                      </span>
                                    </div>
                                    <Award className="w-4 h-4 text-green-600" />
                                  </div>
                                  <h4 className="font-semibold text-gray-900 mb-2">
                                    {response.question}
                                  </h4>
                                  <p className="text-sm text-gray-600">
                                    {response.feedback}
                                  </p>
                                </div>
                              )
                            )
                          ) : (
                            <div className="border border-gray-200 rounded-lg p-6 bg-gray-50 text-center">
                              <Award className="w-8 h-8 text-gray-400 mx-auto mb-3" />
                              <p className="text-gray-500 font-medium mb-1">
                                No standout responses identified
                              </p>
                            </div>
                          )}
                        </div>
                      </div>

                      {/* Weakest Responses */}
                      <div className="bg-white rounded-xl shadow-xs p-6">
                        <h2 className="text-xl font-bold text-gray-900 mb-6 flex items-center gap-2">
                          <TrendingDown className="w-5 h-5 text-red-600" />
                          Areas for Improvement
                        </h2>
                        <div className="space-y-4">
                          {sessionAnalysisData.weakestResponses &&
                          sessionAnalysisData.weakestResponses.length > 0 ? (
                            sessionAnalysisData.weakestResponses.map(
                              (response, index) => (
                                <div
                                  key={index}
                                  className="border border-red-100 rounded-lg p-4 bg-red-50/30"
                                >
                                  <div className="flex items-start justify-between mb-2">
                                    <div className="flex items-center gap-2">
                                      <span className="bg-red-600 text-white text-xs px-2 py-1 rounded-full font-semibold">
                                        #{index + 1}
                                      </span>
                                      <span
                                        className={`px-2 py-1 rounded-full text-sm font-semibold ${getScoreColor(
                                          response.score
                                        )}`}
                                      >
                                        {response.score}%
                                      </span>
                                    </div>
                                    <AlertTriangle className="w-4 h-4 text-red-600" />
                                  </div>
                                  <h4 className="font-semibold text-gray-900 mb-2">
                                    {response.question}
                                  </h4>
                                  <p className="text-sm text-gray-600">
                                    {response.feedback}
                                  </p>
                                </div>
                              )
                            )
                          ) : (
                            <div className="border border-gray-200 rounded-lg p-6 bg-gray-50 text-center">
                              <TrendingUp className="w-8 h-8 text-gray-400 mx-auto mb-3" />
                              <p className="text-gray-500 font-medium mb-1">
                                No significant areas for improvement identified
                              </p>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>

                    {/* Topic Proficiency Breakdown */}
                    <div className="bg-white rounded-xl shadow-xs p-6">
                      <div className=" flex flex-col gap-2 mb-6">
                        <h2 className="text-xl font-bold text-gray-900 flex items-center gap-2">
                          <Target className="w-5 h-5 text-blue-600" />
                          Topic Proficiency Breakdown
                        </h2>

                        <p className="text-sm text-gray-600">
                          This section provides an overview of your proficiency
                          in various topics based on the session analysis.
                        </p>
                      </div>

                      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                        {sessionAnalysisData.topicProficiencies &&
                        sessionAnalysisData.topicProficiencies.length > 0 ? (
                          sessionAnalysisData.topicProficiencies.map(
                            (topic, index) => (
                              <div
                                key={index}
                                className="border border-gray-100 rounded-lg p-6 hover:shadow-md transition-shadow"
                              >
                                <div className="flex items-center justify-between mb-4">
                                  <div className="flex flex-col gap-1">
                                    <h3 className="text-lg font-semibold text-gray-900">
                                      {topic.topic}
                                    </h3>
                                    <div className="text-sm text-gray-500">
                                      {topic.subText}
                                    </div>
                                  </div>
                                  <div className="flex items-center gap-2">
                                    <span
                                      className={`px-3 py-1 rounded-full text-sm font-semibold ${getScoreColor(
                                        topic.score
                                      )}`}
                                    >
                                      {topic.score}%
                                    </span>
                                    <span className="text-xs text-gray-500">
                                      ({topic.questionsAsked} questions)
                                    </span>
                                  </div>
                                </div>

                                <div className="w-full bg-gray-200 rounded-full h-2 mb-4">
                                  <div
                                    className={`h-2 rounded-full transition-all duration-700 ${getScoreBarColor(
                                      topic.score
                                    )}`}
                                    style={{ width: `${topic.score}%` }}
                                  ></div>
                                </div>

                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                  <div>
                                    <h4 className="text-sm font-semibold text-green-700 mb-2 flex items-center gap-1">
                                      <TrendingUp className="w-3 h-3" />
                                      Strengths
                                    </h4>
                                    <ul className="text-xs text-gray-600 space-y-1">
                                      {topic.strengths &&
                                      topic.strengths.length > 0 ? (
                                        topic.strengths.map((strength, idx) => (
                                          <li
                                            key={idx}
                                            className="flex items-start gap-1"
                                          >
                                            <span className="text-green-500 mt-0.5">
                                              •
                                            </span>
                                            {strength}
                                          </li>
                                        ))
                                      ) : (
                                        <li className="text-gray-400 italic text-center py-2">
                                          No specific strengths identified
                                        </li>
                                      )}
                                    </ul>
                                  </div>

                                  <div>
                                    <h4 className="text-sm font-semibold text-orange-700 mb-2 flex items-center gap-1">
                                      <AlertTriangle className="w-3 h-3" />
                                      Improvements
                                    </h4>
                                    <ul className="text-xs text-gray-600 space-y-1">
                                      {topic.improvements &&
                                      topic.improvements.length > 0 ? (
                                        topic?.improvements?.map(
                                          (improvement, idx) => (
                                            <li
                                              key={idx}
                                              className="flex items-start gap-1"
                                            >
                                              <span className="text-orange-500 mt-0.5">
                                                •
                                              </span>
                                              {improvement}
                                            </li>
                                          )
                                        )
                                      ) : (
                                        <li className="text-gray-400 italic text-center py-2">
                                          No specific improvements identified
                                        </li>
                                      )}
                                    </ul>
                                  </div>
                                </div>
                              </div>
                            )
                          )
                        ) : (
                          <div className="col-span-full border border-gray-200 rounded-lg p-8 bg-gray-50 text-center">
                            <Target className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                            <p className="text-gray-500 font-medium mb-2">
                              No topic proficiency data available
                            </p>
                            <p className="text-sm text-gray-400">
                              Topics will be analyzed as more interview data
                              becomes available
                            </p>
                          </div>
                        )}
                      </div>
                    </div>
                  </>
                )}
              </>
            </RequestWrapper>
            {/* </div> */}
          </div>
        </div>

        {/* Sticky bottom section - now outside the scrollable area */}
        <div className="bg-white border-t border-gray-200 py-2 px-6">
          <div
            role="button"
            onClick={onStartNewSession}
            className="w-3/5 p-4 text-center text-white duration-150 bg-[#0076e9] mx-auto rounded-xl hover:bg-[#022e58]"
          >
            Start a New Session
          </div>
        </div>

        <FeedbackModal
          isOpen={feedbackOpen}
          onClose={feedbackOnClose}
          title="How was your interview experience with FiBi?"
          description="Please rate your interview session and provide any feedback to help us improve."
          ratingType="emoji" // or "stars"
          sessionCount={Number(sessionTemp) as number}
          businessId={fibiClientToken as string}
        />
      </motion.div>
    </Overlay>,
    document.body
  );
};

export default AnalyticsModal;
